/**
 * Office Sidebar - Composant principal du taskpane Office Add-in
 * Implémente les zones du PRD : Context Bar, Quick Actions, Thread, Input Bar, Footer CTA
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useOfficeContext } from './useOfficeContext';
import { useOfficeAuth } from './useOfficeAuth';
import OfficeMessageContent from './OfficeMessageContent';
import {
    officeService,
    ChatMessage,
    HandoffPayload,
    StakeholderInfo,
} from '../../../services/api/officeService';
import { openFamlaWebUrl } from './microsoftDesktopLaunch';
import { Spiner } from '../../../components';
import { config } from '../../../services/config';
import { getTenantName } from '../../../utils/brandingStyles';
import OfficeChatTurn from './OfficeChatTurn';
import './OfficeSidebar.css';

type ChatMode = 'qa' | 'andon';

interface SourceChip {
    processMapName?: string;
    stepNumber?: number;
    documentName?: string;
    deepLink?: string;
}

interface ThreadMessage extends ChatMessage {
    sourceChip?: SourceChip;
    isGeneralKnowledge?: boolean;
    /** Ribbon deep-link map flow — renders Context captured card */
    mapBootstrap?: boolean;
}

function parseUrlMode(): ChatMode | null {
    const modeParam = new URLSearchParams(window.location.search).get('mode');
    if (modeParam === 'map') return 'qa';
    if (modeParam === 'andon') return 'andon';
    return null;
}

const OfficeSidebar: React.FC = () => {
    const { t } = useTranslation();
    const stOnlyMode = config.app.stOnlyMode;
    const {
        isAuthenticated,
        userAzureId,
        userEmail,
        userName,
        accessToken,
        signIn,
        signInWithEmail,
        signInWithMicrosoft,
        authMethod,
        hasEntraToken,
        isLoading: authLoading,
        authError,
    } = useOfficeAuth();
    const emailPasswordAuth = config.app.officeEmailPasswordAuth && !stOnlyMode;
    const { isOfficeReady, isLoading: contextLoading, context, refreshContext } = useOfficeContext();

    const [messages, setMessages] = useState<ThreadMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentMode, setCurrentMode] = useState<ChatMode | null>(null);
    const [isHandoffLoading, setIsHandoffLoading] = useState(false);
    const [stakeholders, setStakeholders] = useState<StakeholderInfo[]>([]);
    const [selectedStakeholders, setSelectedStakeholders] = useState<Record<string, boolean>>({});
    const [isLoadingStakeholders, setIsLoadingStakeholders] = useState(false);
    const [isInviting, setIsInviting] = useState(false);

    const threadRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const lastAppliedUrlSearch = useRef<string | null>(null);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [messages]);

    const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
        if (isAuthenticated) return true;
        const ok = await signIn();
        if (!ok) {
            toast.error(
                stOnlyMode
                    ? t('office.auth.signInRequiredSt', 'Veuillez vous connecter avec SSO   pour continuer.')
                    : t('office.auth.signInRequired', 'Please sign in with Microsoft to continue.')
            );
        }
        return ok;
    }, [isAuthenticated, signIn, stOnlyMode, t]);

    const handleHandoff = useCallback(async () => {
        if (isHandoffLoading) return;

        const ok = await ensureAuthenticated();
        if (!ok) return;

        setIsHandoffLoading(true);

        try {
            const hostApp = context?.hostApp || 'Unknown';
            const hostPlatform = context?.hostPlatform || 'Unknown';

            const payload: HandoffPayload = {
                context: {
                    hostApp,
                    hostPlatform,
                    contextText: context?.contextText || '',
                    contextType: context?.contextType || 'unknown',
                    subject: context?.subject,
                    documentTitle: context?.documentTitle,
                    cellRange: context?.cellRange,
                    emailBody: context?.emailBody,
                    recipients: context?.recipients,
                    outlookItemId: context?.outlookItemId,
                    conversationId: context?.conversationId,
                    desktopReturnUrl: context?.desktopReturnUrl,
                },
                threadMessages: messages,
                userAzureId,
                userEmail,
                userName,
            };

            const { sessionToken } = await officeService.createHandoff(payload);

            const returnPath = `/map/new?session=${sessionToken}`;
            const webAppUrl = stOnlyMode
                ? `${window.location.origin}/st-portal?redirect=${encodeURIComponent(returnPath)}`
                : `${window.location.origin}/auth/login?redirect=${encodeURIComponent(returnPath)}`;
            openFamlaWebUrl(webAppUrl);
        } catch (err) {
            toast.error(t('office.sidebar.handoffError', 'Failed to create handoff session'));
        } finally {
            setIsHandoffLoading(false);
        }
    }, [
        context,
        messages,
        userAzureId,
        userEmail,
        userName,
        isHandoffLoading,
        ensureAuthenticated,
        stOnlyMode,
        t,
    ]);

    const bootstrapMapFlow = useCallback(async () => {
        const ok = await ensureAuthenticated();
        if (!ok) return;

        setCurrentMode('qa');
        setMessages([
            {
                id: 'assistant-map-bootstrap',
                role: 'assistant',
                content: t(
                    'office.sidebar.mapWelcome',
                    "Captured this context. I'll hand off to the Famla web app and pre-load the document-to-map generator."
                ),
                timestamp: new Date().toISOString(),
                mapBootstrap: true,
            },
        ]);
    }, [ensureAuthenticated, t]);

    const bootstrapAndonFlow = useCallback(async () => {
        const ok = await ensureAuthenticated();
        if (!ok) return;

        setCurrentMode('andon');
        setMessages([
            {
                id: 'assistant-andon-bootstrap',
                role: 'assistant',
                content: t(
                    'office.sidebar.andonWelcome',
                    "You're flagging a pain point. In one line, what went wrong here?"
                ),
                timestamp: new Date().toISOString(),
            },
        ]);
        window.setTimeout(() => inputRef.current?.focus(), 0);
    }, [ensureAuthenticated, t]);

    const handleQuickAction = useCallback(async (mode: ChatMode) => {
        const ok = await ensureAuthenticated();
        if (!ok) return;

        if (parseUrlMode() === mode) {
            if (mode === 'qa') {
                await bootstrapMapFlow();
            } else {
                await bootstrapAndonFlow();
            }
            return;
        }

        setCurrentMode(mode);

        const systemMessage: ThreadMessage = {
            id: `sys-mode-${mode}`,
            role: 'system',
            content: mode === 'qa'
                ? t('office.sidebar.modeQa', 'Ask me anything about this process.')
                : t('office.sidebar.modeAndon', 'Describe the issue or pain point you encountered.'),
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [
            ...prev.filter((m) => !(m.role === 'system' && m.id.startsWith('sys-'))),
            systemMessage,
        ]);
        inputRef.current?.focus();
    }, [ensureAuthenticated, bootstrapMapFlow, bootstrapAndonFlow, t]);

    // Deep-link from manifest ribbon: /embedded/sidebar?mode=map|andon
    useEffect(() => {
        if (!isAuthenticated || !isOfficeReady) return;

        const urlSearch = window.location.search;
        const urlMode = parseUrlMode();
        if (!urlMode) {
            lastAppliedUrlSearch.current = null;
            return;
        }
        if (lastAppliedUrlSearch.current === urlSearch) return;
        lastAppliedUrlSearch.current = urlSearch;

        if (urlMode === 'qa') {
            void bootstrapMapFlow();
        } else {
            void bootstrapAndonFlow();
        }
    }, [isAuthenticated, isOfficeReady, bootstrapMapFlow, bootstrapAndonFlow]);

    // PRD §5.3 — load Outlook recipients as stakeholder checkboxes
    useEffect(() => {
        const loadStakeholders = async () => {
            if (!isAuthenticated) return;
            if (context?.hostApp !== 'Outlook' || !context?.outlookItemId) {
                setStakeholders([]);
                setSelectedStakeholders({});
                return;
            }
            setIsLoadingStakeholders(true);
            try {
                let list: StakeholderInfo[] = [];
                if (stOnlyMode || authMethod === 'password' || !hasEntraToken) {
                    const emails = context.recipients || [];
                    list = emails.map((email) => ({
                        email,
                        displayName: email.split('@')[0],
                    }));
                } else if (accessToken) {
                    list = await officeService.discoverStakeholders(
                        context.outlookItemId,
                        accessToken
                    );
                }
                setStakeholders(list);
                const initial: Record<string, boolean> = {};
                list.forEach((s) => {
                    initial[s.email.toLowerCase()] = false;
                });
                setSelectedStakeholders(initial);
            } catch {
                setStakeholders([]);
            } finally {
                setIsLoadingStakeholders(false);
            }
        };
        void loadStakeholders();
    }, [isAuthenticated, accessToken, authMethod, hasEntraToken, context?.hostApp, context?.outlookItemId, context?.recipients, stOnlyMode]);

    const handleInviteStakeholders = useCallback(async () => {
        const selected = stakeholders.filter(
            (s) => selectedStakeholders[s.email.toLowerCase()]
        );
        if (!selected.length || isInviting) return;

        setIsInviting(true);
        try {
            const result = await officeService.inviteContributorsPublic({
                processName: context?.subject || context?.contextText || 'Process discovery',
                stepLabel: 'Email thread role',
                inviterEmail: userEmail,
                inviterAzureId: userAzureId,
                contributors: selected.map((s) => ({
                    email: s.email,
                    displayName: s.displayName,
                    azureId: s.azureId,
                })),
            });
            const sent = (result as any)?.sent ?? 0;
            toast.success(
                t('office.sidebar.inviteSuccess', '{{count}} stakeholder(s) invited in Teams', {
                    count: sent,
                })
            );
        } catch (err: any) {
            toast.error(
                err?.message ||
                    t('office.sidebar.inviteError', 'Failed to invite stakeholders in Teams')
            );
        } finally {
            setIsInviting(false);
        }
    }, [
        stakeholders,
        selectedStakeholders,
        isInviting,
        context,
        userEmail,
        userAzureId,
        t,
    ]);

    const handleSendMessage = useCallback(async () => {
        const trimmed = inputValue.trim();
        if (!trimmed || isSubmitting) return;

        const ok = await ensureAuthenticated();
        if (!ok) return;

        const userMessage: ThreadMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: trimmed,
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsSubmitting(true);

        try {
            if (currentMode === 'andon') {
                await officeService.flagPainPoint({
                    hostApp: context?.hostApp || 'Unknown',
                    contextText: context?.contextText || '',
                    contextType: context?.contextType || 'unknown',
                    subject: context?.subject,
                    userMessage: trimmed,
                    userAzureId,
                    userEmail,
                    userName,
                });

                const confirmMessage: ThreadMessage = {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: t('office.sidebar.andonConfirm', 'Your pain point has been flagged. The process owner and administrators have been notified via Teams and email.'),
                    timestamp: new Date().toISOString(),
                };

                setMessages((prev) => [...prev, confirmMessage]);
                toast.success(t('office.sidebar.andonSuccess', 'Pain point flagged successfully'));
            } else {
                const response = await officeService.askAboutProcess({
                    hostApp: context?.hostApp || 'Unknown',
                    contextText: context?.contextText || '',
                    contextType: context?.contextType || 'unknown',
                    question: trimmed,
                    threadHistory: messages.filter((m) => m.role !== 'system'),
                    userAzureId,
                    userEmail,
                });

                const generalKnowledgePrefix = response.isGeneralKnowledge
                    ? t('office.sidebar.generalKnowledge', 'Based on general practice, not a specific process record: ')
                    : '';

                const assistantMessage: ThreadMessage = {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: `${generalKnowledgePrefix}${response.answer}`,
                    timestamp: new Date().toISOString(),
                    isGeneralKnowledge: response.isGeneralKnowledge,
                    sourceChip: response.source ? {
                        processMapName: response.source.processMapName,
                        stepNumber: response.source.stepNumber,
                        documentName: response.source.documentName,
                        deepLink: response.source.deepLink,
                    } : undefined,
                };

                setMessages((prev) => [...prev, assistantMessage]);
            }
        } catch (err) {
            const errorMessage: ThreadMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: t('office.sidebar.error', 'An error occurred. Please try again.'),
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, errorMessage]);
            toast.error(t('office.sidebar.errorToast', 'Request failed'));
        } finally {
            setIsSubmitting(false);
        }
    }, [
        inputValue,
        isSubmitting,
        currentMode,
        context,
        messages,
        userAzureId,
        userEmail,
        userName,
        ensureAuthenticated,
        t,
    ]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    const renderContextCapturedCard = () => (
        <div className="office-sidebar__context-card">
            <div className="office-sidebar__context-card-header">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>{t('office.sidebar.contextCapturedTitle', 'Context captured')}</span>
            </div>
            <p className="office-sidebar__context-card-body">
                {t(
                    'office.sidebar.contextCapturedBody',
                    'Opening the Famla web app with the document-to-map generator pre-loaded.'
                )}
            </p>
            <button
                type="button"
                className="office-sidebar__context-card-btn"
                onClick={() => void handleHandoff()}
                disabled={isHandoffLoading}
            >
                {isHandoffLoading
                    ? t('office.sidebar.handoffLoading', 'Creating session...')
                    : t('office.sidebar.openWebApp', 'Open Famla web app')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
            </button>
        </div>
    );

    const renderSourceChip = (chip: SourceChip) => {
        const label = chip.processMapName
            ? `Source: ${chip.processMapName}${chip.stepNumber ? `, step ${chip.stepNumber}` : ''}`
            : chip.documentName
                ? `Source: ${chip.documentName}`
                : null;

        if (!label) return null;

        const handleChipClick = () => {
            if (!chip.deepLink) {
                toast(t('office.sidebar.sourceChipClick', 'Opening source in new tab...'));
                return;
            }

            try {
                if (
                    typeof Office !== 'undefined' &&
                    Office.context?.ui?.openBrowserWindow
                ) {
                    Office.context.ui.openBrowserWindow(chip.deepLink);
                } else {
                    window.open(chip.deepLink, '_blank');
                }
            } catch {
                window.open(chip.deepLink, '_blank');
            }
        };

        return (
            <button className="office-sidebar__source-chip" onClick={handleChipClick} type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>{label}</span>
            </button>
        );
    };

    if (!isOfficeReady && !contextLoading) {
        return (
            <div className="office-sidebar office-sidebar--error">
                <div className="office-sidebar__error-content">
                    <h2>{t('office.sidebar.notAvailable', 'Office not available')}</h2>
                    <p>{t('office.sidebar.notAvailableDesc', 'This sidebar must be opened from within Microsoft Office.')}</p>
                </div>
            </div>
        );
    }

    if (authLoading) {
        return (
            <div className="office-sidebar office-sidebar--loading">
                <Spiner className="d-flex justify-content-center spinner-border-sm" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="office-sidebar">
                <div className="office-sidebar__thread" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                        <p style={{ marginBottom: 16, color: '#666' }}>
                            {stOnlyMode
                                ? t('office.auth.signInPromptSt', 'Connectez-vous avec votre compte ST pour utiliser Famla.')
                                : emailPasswordAuth
                                    ? t('office.auth.signInPromptEmail', 'Sign in with your Famla email and password.')
                                    : t('office.auth.signInPrompt', 'Sign in with your Microsoft account to use Famla.')}
                        </p>
                        {emailPasswordAuth ? (
                            <>
                                <button
                                    className="office-sidebar__cta-btn"
                                    onClick={() => void signInWithEmail()}
                                    disabled={authLoading}
                                    style={{ width: '100%' }}
                                >
                                    {t('office.auth.signInButtonEmail', 'Sign in with email')}
                                </button>
                                <button
                                    type="button"
                                    className="office-sidebar__link-btn"
                                    onClick={() => void signInWithMicrosoft()}
                                    disabled={authLoading}
                                    style={{
                                        width: '100%',
                                        marginTop: 12,
                                        background: 'transparent',
                                        border: '1px solid #c8c6c4',
                                        borderRadius: 4,
                                        padding: '10px 16px',
                                        cursor: 'pointer',
                                        color: '#323130',
                                    }}
                                >
                                    {t('office.auth.signInWithMicrosoftSecondary', 'Sign in with Microsoft')}
                                </button>
                            </>
                        ) : (
                            <button
                                className="office-sidebar__cta-btn"
                                onClick={() => void signIn()}
                                disabled={authLoading}
                                style={{ width: '100%' }}
                            >
                                {stOnlyMode
                                    ? t('office.auth.signInButtonSt', 'Se connecter avec SSO ST')
                                    : t('office.auth.signInButton', 'Sign in with Microsoft')}
                            </button>
                        )}
                        {authError && (
                            <p style={{ marginTop: 12, color: '#b42318', fontSize: 13 }}>
                                {authError}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="office-sidebar">
            {/* Zone 1: Context Bar */}
            <div className="office-sidebar__context-bar">
                <span className="office-sidebar__context-label">
                    {t('office.sidebar.readingContext', 'Reading context')}
                </span>
                <span className="office-sidebar__context-text" title={context?.contextText || ''}>
                    {contextLoading
                        ? t('office.sidebar.loading', 'Loading...')
                        : context?.contextText || t('office.sidebar.noContext', 'No context available')}
                </span>
            </div>

            {/* Zone 3: Quick Actions */}
            <div className="office-sidebar__quick-actions">
                <button
                    className={`office-sidebar__action-btn ${currentMode === 'qa' ? 'office-sidebar__action-btn--active' : ''}`}
                    onClick={() => handleQuickAction('qa')}
                    disabled={!isOfficeReady}
                >
                    {t('office.sidebar.askAboutProcess', 'Ask about this process')}
                </button>
                <button
                    className={`office-sidebar__action-btn ${currentMode === 'andon' ? 'office-sidebar__action-btn--active' : ''}`}
                    onClick={() => handleQuickAction('andon')}
                    disabled={!isOfficeReady}
                >
                    {t('office.sidebar.flagPainPoint', 'Flag a pain point')}
                </button>
            </div>

            {/* PRD §5.3 — Stakeholder discovery checkboxes */}
            {context?.hostApp === 'Outlook' && (stakeholders.length > 0 || isLoadingStakeholders) && (
                <div className="office-sidebar__stakeholders">
                    <div className="office-sidebar__stakeholders-title">
                        {t('office.sidebar.stakeholders', 'Process participants')}
                    </div>
                    {isLoadingStakeholders ? (
                        <span className="office-sidebar__stakeholders-hint">
                            {t('office.sidebar.loadingStakeholders', 'Loading recipients…')}
                        </span>
                    ) : (
                        <>
                            <ul className="office-sidebar__stakeholders-list">
                                {stakeholders.map((s) => {
                                    const key = s.email.toLowerCase();
                                    return (
                                        <li key={key}>
                                            <label className="office-sidebar__stakeholder-item">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(selectedStakeholders[key])}
                                                    onChange={(e) =>
                                                        setSelectedStakeholders((prev) => ({
                                                            ...prev,
                                                            [key]: e.target.checked,
                                                        }))
                                                    }
                                                />
                                                <span>
                                                    {s.displayName || s.email}
                                                    {s.displayName ? (
                                                        <small> · {s.email}</small>
                                                    ) : null}
                                                </span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                            <button
                                type="button"
                                className="office-sidebar__action-btn"
                                disabled={
                                    isInviting ||
                                    !Object.values(selectedStakeholders).some(Boolean)
                                }
                                onClick={handleInviteStakeholders}
                            >
                                {isInviting
                                    ? t('office.sidebar.inviting', 'Inviting…')
                                    : t('office.sidebar.inviteTeams', 'Invite in Teams')}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Zone 4: Message Thread (flexible, scrollable) */}
            <div className="office-sidebar__thread office-interview-thread" ref={threadRef}>
                {messages.length === 0 ? (
                    <div className="office-sidebar__thread-empty">
                        <p>{t('office.sidebar.emptyThread', 'Select an action above to start.')}</p>
                        <p>{t('office.sidebar.emptyThreadHint', 'Ask a question or flag a pain point about this email.')}</p>
                    </div>
                ) : (
                    messages.map((msg) => {
                        if (msg.role === 'system') {
                            return (
                                <div key={msg.id} className="office-sidebar__message--system">
                                    <span className="t-hint text-center w-100">{msg.content}</span>
                                </div>
                            );
                        }

                        const isAi = msg.role === 'assistant';
                        const variant = isAi ? 'ai' : 'human';
                        const senderName = isAi ? getTenantName() : (userName || userEmail || 'User');

                        return (
                            <OfficeChatTurn
                                key={msg.id}
                                variant={variant}
                                senderName={senderName}
                                timestamp={msg.timestamp}
                                footer={
                                    isAi && msg.mapBootstrap
                                        ? renderContextCapturedCard()
                                        : isAi && msg.sourceChip
                                            ? renderSourceChip(msg.sourceChip)
                                            : undefined
                                }
                            >
                                <OfficeMessageContent
                                    content={msg.content}
                                    formatted={isAi}
                                />
                            </OfficeChatTurn>
                        );
                    })
                )}
                {isSubmitting && (
                    <OfficeChatTurn
                        variant="ai"
                        senderName={getTenantName()}
                        timestamp={new Date().toISOString()}
                        isTyping={true}
                    >
                        <div className="office-sidebar__typing-indicator">
                            <span></span><span></span><span></span>
                        </div>
                    </OfficeChatTurn>
                )}
            </div>

            {/* Zone 5: Input Bar (48px) */}
            <div className="office-sidebar__input-bar">
                <div
                    className={`office-sidebar__input-wrap${!currentMode ? ' office-sidebar__input-wrap--locked' : ''}`}
                >
                    {!currentMode && (
                        <>
                            <div className="office-sidebar__input-hint" role="tooltip">
                                {t(
                                    'office.sidebar.chooseModeHint',
                                    'Choose “Ask about this process” or “Flag a pain point” above first.'
                                )}
                            </div>
                            {/* Captures hover over disabled textarea (pointer-events: none on disabled inputs) */}
                            <div className="office-sidebar__input-lock" aria-hidden="true" />
                        </>
                    )}
                    <textarea
                        ref={inputRef}
                        className="office-sidebar__input"
                        placeholder={t('office.sidebar.inputPlaceholder', 'Ask a question or describe an irritant')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={!currentMode || isSubmitting}
                        rows={1}
                    />
                </div>
                <button
                    className="office-sidebar__send-btn"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || !currentMode || isSubmitting}
                    aria-label={t('office.sidebar.send', 'Send')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="19" x2="12" y2="5" />
                        <polyline points="5 12 12 5 19 12" />
                    </svg>
                </button>
            </div>

            {/* Zone 6: Footer CTA (48px) */}
            <footer className="office-sidebar__footer">
                <button
                    className="office-sidebar__cta-btn"
                    onClick={handleHandoff}
                    disabled={isHandoffLoading}
                >
                    {isHandoffLoading
                        ? t('office.sidebar.handoffLoading', 'Creating session...')
                        : t('office.sidebar.generateMap', 'Generate a full process map in Famla')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </button>
            </footer>
        </div>
    );
};

export default OfficeSidebar;
